import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import AdvisorsPicker from "../../../../components/enrollment/advisorsPicker";
import PhoneField from "../../../../components/phoneField";
import SchedulePicker from "../../../../components/enrollment/schedulePicker";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import useEsTelefono from "../../../../utils/esTelefono";
import { nombrePropioTexto, primeraMayuscula, textoPropio } from "../../../../utils/textoPropio";
import {
	AdvisorSelection, AvailableScheduleDTO, EnrollmentRequestDetailsDTO, ExistingStudentDTO, RequestDocumentDTO, RequestPaymentDTO, RequestStudentData,
	PAYMENT_INTENT_TYPE, REQUEST_DOCUMENT_TYPE, REQUEST_MODE, REQUEST_STATUS
} from "../../../../models/EnrollmentHistoryDTO";
import S from "./strings";
import { mismoTelefono, telefonoIncompleto } from "../../../../utils/mismoTelefono";
import { correoInvalido } from "../../../../utils/correoInvalido";
import { ACCEPTED_FILES, MAX_UPLOAD_BYTES, errorText, isMinor, todayIso, problemaIdentificacion } from "./helpers";
import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import FotoCelular, { TipoCaptura } from "../../../../components/enrollment/fotoCelular";
import useCatalogosAlumno from "../../../../components/enrollment/catalogosAlumno";
import PasoPromocion from "./pasoPromocion";
import { RequestPromotionDTO } from "../../../../models/EnrollmentHistoryDTO";
import { useUnaALaVez } from "../../../../utils/unaALaVez";
import { sinTalleresNiEventos } from "../../../../components/enrollment/cursosNoMatriculables";

export interface RequestFormModalProps {
	campusId: number;
	countryId: number;
	/** null = new request */
	requestId: number | null;
	initialModeId: number;
	canPresencial: boolean;
	/** Admisiones: lo que llena se matricula al guardar (Dirección de Ventas elige En Persona pero pasa por revisión). */
	esAdmisiones?: boolean;
	onClose: () => void;
	onSaved: (details: EnrollmentRequestDetailsDTO, action: "saved" | "submitted" | "approved" | "resent") => void;
	/** "Completar" una persona de una pre-solicitud: la solicitud nueva sale llena con lo que capturó la asesora. */
	prefill?: RequestPrefill | null;
	/** Otra persona que se matricula junto con esta solicitud (paso Promoción → "Agregar otra persona"). */
	bundleWith?: { requestId: number; name: string } | null;
	/** Abrir otra solicitud (una nueva junto con esta, o una de las que ya van juntas). */
	onSwitch?: (target: { requestId: number | null; bundleWith?: { requestId: number; name: string } }) => void;
}

export interface RequestPrefill {
	prerequestId: number;
	personId: number;
	phone: string;
	name: string | null;
	/** Los cursos y horarios que eligió la asesora (pueden ser varios o ninguno). */
	scheduleIds: number[];
	advisors: AdvisorSelection[];
	notes: string | null;
	/** Datos del alumno que mandó la asesora (opcionales): llenan los pasos para que la supervisora solo revise. */
	studentData?: RequestStudentData | null;
	/** Comprobantes de la venta e identificación del cliente: el servidor los copia a la solicitud al guardarla. */
	documentos: { id: number; name: string; typeId: number }[];
	verDocumento: (documentId: number) => void;
}

/** "Carlos Mejía López" → Nombres "Carlos", Apellidos "Mejía López"; con cuatro palabras, dos y dos. */
const partirNombre = (completo: string | null): { firstName: string; lastName: string } => {
	const w = String(completo || "").trim().split(/\s+/).filter(Boolean);
	if (w.length < 2) return { firstName: w[0] || "", lastName: "" };
	const n = w.length >= 4 ? 2 : 1;
	return { firstName: w.slice(0, n).join(" "), lastName: w.slice(n).join(" ") };
};

interface PendingFile { typeId: number; file: File; }


const emptyPayment = (): RequestPaymentDTO => ({ typeId: null, amount: null, recipientAccountId: null, paymentMethodId: null, reference: null, paidAt: null });


const Field: React.FC<{ label: string; required?: boolean; missing?: boolean; className?: string; children: React.ReactNode }> = ({ label, required, missing, className, children }) => (
	<div className={(className || "col-md-6") + (missing ? " waPasoFalta" : "")} style={{ marginBottom: 12 }}>
		<label style={{ position: "static", fontSize: 14, color: missing ? "var(--wa-oro)" : "var(--wa-apagado)", display: "block", marginBottom: 3, fontWeight: 500 }}>
			{label}{required ? " *" : ""}{missing ? " · falta" : ""}
		</label>
		{children}
	</div>
);

/*
	El formulario va por pasos: una pregunta corta a la vez, con barra de avance y palomita en cada
	paso terminado. Antes era una sola pared de 30 campos y asustaba, sobre todo en el celular,
	que es donde lo llenan (Esteban, 2026-09-23).
*/
type PasoKey = "inicio" | "alumno" | "contacto" | "domicilio" | "estudios" | "emergencia" | "cursos" | "promocion" | "documentos" | "revisar";

const PASOS: { key: PasoKey; titulo: string; pregunta: string; icono: any; soloNuevo?: boolean }[] = [
	{ key: "inicio", titulo: "Inicio", pregunta: "¿A quién vamos a inscribir?", icono: solid("flag-checkered") },
	{ key: "alumno", titulo: "Alumno", pregunta: "¿Quién es?", icono: solid("id-card"), soloNuevo: true },
	{ key: "contacto", titulo: "Contacto", pregunta: "¿Cómo lo contactamos?", icono: solid("phone"), soloNuevo: true },
	{ key: "domicilio", titulo: "Domicilio", pregunta: "¿Dónde vive?", icono: solid("location-dot"), soloNuevo: true },
	{ key: "estudios", titulo: "Estudios", pregunta: "¿Qué ha estudiado?", icono: solid("graduation-cap"), soloNuevo: true },
	{ key: "emergencia", titulo: "Emergencia", pregunta: "¿A quién llamamos en una emergencia?", icono: solid("heart-pulse"), soloNuevo: true },
	{ key: "cursos", titulo: "Cursos", pregunta: "¿Qué va a estudiar?", icono: solid("book-open") },
	{ key: "promocion", titulo: "Promoción", pregunta: "¿Con qué promoción?", icono: solid("tags") },
	{ key: "documentos", titulo: "Documentos", pregunta: "Sube los documentos", icono: solid("paperclip") },
	{ key: "revisar", titulo: "Revisar", pregunta: "Revisa y envía", icono: solid("circle-check") }
];

/** A qué paso manda cada aviso del servidor, para llevar a la supervisora directo a arreglarlo. */
const pasoDeAviso = (aviso: string): PasoKey => {
	const t = aviso.toLowerCase();
	if (/^promoci/.test(t)) return "promocion";
	if (/nota de matr|horario|curso/.test(t)) return "cursos";
	if (/comprobante|identificaci[oó]n del|documento|archivo/.test(t)) return "documentos";
	if (/tutor|contacto|emergencia|parentesco|padre|madre/.test(t)) return "emergencia";
	if (/escuela|grado|ocupaci/.test(t)) return "estudios";
	if (/estado|ciudad|direcci|pa[ií]s|nacionalidad|postal|departamento/.test(t)) return "domicilio";
	if (/correo|tel[eé]fono/.test(t)) return "contacto";
	if (/nombre|apellido|nacimiento|g[eé]nero|identificaci/.test(t)) return "alumno";
	return "revisar";
};

const vacio = (v: any) => v === null || v === undefined || String(v).trim() === "";

/**
 * Create / edit a sign-up request. Ventas fills it for remote sales; Admisiones can also fill it (remote or presencial).
 * Nothing here touches kits or discounts: commercial terms stay free text until the discounts redesign lands.
 */
/** El paso Promoción tal como lo guardó el servidor (sin «approve», que solo vive mientras se edita). */
const promoDe = (d: any): RequestPromotionDTO => d && d.promotion
	? { items: d.promotion.items || [], referredBy: d.promotion.referredBy || null, override10: d.promotion.override10 || null }
	: { items: [] };

const RequestFormModal: React.FC<RequestFormModalProps> = ({ campusId, countryId, requestId, initialModeId, canPresencial, esAdmisiones, onClose, onSaved, prefill, bundleWith, onSwitch }) => {
	const http = HttpManager.getInstance();
	const basicInfo: any = useSelector((state: RootState) => state.basicInfo.value);

	const [loading, setLoading] = useState<boolean>(requestId !== null);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [issues, setIssues] = useState<string[]>([]);
	const [details, setDetails] = useState<EnrollmentRequestDetailsDTO | null>(null);

	const [modeId, setModeId] = useState<number>(initialModeId);
	const [studentKind, setStudentKind] = useState<"existing" | "new">("new");
	const [existingStudent, setExistingStudent] = useState<ExistingStudentDTO | null>(null);
	const [studentQuery, setStudentQuery] = useState<string>("");
	const [studentResults, setStudentResults] = useState<ExistingStudentDTO[] | null>(null);
	const datosVacios = (): RequestStudentData => ({
		countryId: countryId, nationalityId: countryId, tagIds: [], contacts: [{ name: "", relation: "", contactMedia: "" }], links: { facebook: "", instagram: "" },
		...(basicInfo?.countryRef?.["C_" + countryId] ? { identificationTypeId: basicInfo.countryRef["C_" + countryId].preferred_identification_type_id || "" } : {}),
		...(prefill ? { phone1: prefill.phone, ...partirNombre(prefill.name), ...(prefill.studentData || {}) } : {})
	});
	const [studentData, setStudentData] = useState<RequestStudentData>(datosVacios);
	const [schedules, setSchedules] = useState<AvailableScheduleDTO[] | null>(null);
	const [scheduleFilter, setScheduleFilter] = useState<string>("");
	//Una solicitud puede llevar varios cursos, cada uno con su propia nota (Esteban, 2026-09-22).
	// La nota de la venta va en el primer horario, como antes.
	const picksIniciales = (): { scheduleId: number; notes: string }[] => prefill ? prefill.scheduleIds.map((id, i) => ({ scheduleId: id, notes: i === 0 ? prefill.notes || "" : "" })) : [];
	const [picks, setPicks] = useState<{ scheduleId: number; notes: string }[]>(picksIniciales);
	//null = aún no elige; [] = eligió "Ninguno" a propósito. Antes se guardaba [] para las dos cosas
	//y "Ninguno" se borraba solo (Esteban, 2026-09-23).
	const [advisors, setAdvisors] = useState<AdvisorSelection[] | null>(prefill && prefill.advisors.length > 0 ? prefill.advisors : null);
	const [payment, setPayment] = useState<RequestPaymentDTO>(emptyPayment());
	//Paso Promoción (Etapa 3 de cobros): obligatorio, con «Ninguna» explícita por curso.
	const [promo, setPromo] = useState<RequestPromotionDTO>({ items: [] });
	const [documents, setDocuments] = useState<RequestDocumentDTO[]>([]);
	const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
	// Guardado automático del borrador (19-oct-2026): la versión que tenemos, para que el otro equipo no se pise.
	const versionRef = useRef<number | null>(null);
	const ultimoGuardado = useRef<string>("");
	const autoTimer = useRef<any>(null);
	const autoEnCurso = useRef<Promise<void> | null>(null);
	const [autoEstado, setAutoEstado] = useState<{ tipo: "guardando" | "guardado" | "error"; texto: string } | null>(null);
	const [celular, setCelular] = useState<TipoCaptura | null>(null);
	// Lo último de cada render, para los temporizadores y la foto que llega del celular (no se quedan con datos viejos).
	const detallesRef = useRef<EnrollmentRequestDetailsDTO | null>(null);
	const pendientesRef = useRef<PendingFile[]>([]);
	pendientesRef.current = pendingFiles;
	const autoGuardarRef = useRef<() => void>(() => { /* se asigna abajo */ });
	/** El id de la matrícula si ya existe (también si el guardado automático la acaba de crear). */
	const idActual = (): number | null => detallesRef.current?.id ?? details?.id ?? requestId ?? null;
	const searchTimer = useRef<any>(null);

	const isPresencial = modeId === REQUEST_MODE.PRESENCIAL;
	/*
		Lo que llena Admisiones se matricula al guardar, En persona o Digital: no se manda a revisión a sí
		misma (Esteban, 2026-09-24). Lo que llena Ventas sigue yendo a la rotación. El servidor decide lo mismo.
	*/
	const yo: any = useSelector((state: RootState) => state.userProfile.value);
	const matriculaDirecta = !!esAdmisiones && (isPresencial || !details || Number(details.createdById) === Number(yo?.id));
	const { states, cities, academicDegrees, identificationType } = useCatalogosAlumno(studentData);

	// ---------- load
	useEffect(() => {
		if (requestId === null) {
			if (basicInfo?.countryRef && basicInfo.countryRef["C_" + countryId] && !prefill?.studentData?.identificationTypeId) {
				setStudentData((d) => ({ ...d, identificationTypeId: basicInfo.countryRef["C_" + countryId].preferred_identification_type_id || "" }));
			}
			return;
		}
		http.enrollmentRequestsService.details(campusId, requestId)
			.then((r) => hydrate(r.data))
			.catch((e) => setError(errorText(e)))
			.finally(() => setLoading(false));
	}, [campusId, requestId]);

	const hydrate = (d: EnrollmentRequestDetailsDTO) => {
		setDetails(d);
		detallesRef.current = d;
		versionRef.current = d.version ?? null;
		setModeId(d.modeId);
		setStudentKind(d.studentId ? "existing" : "new");
		setExistingStudent(d.existingStudent);
		//Reingreso guardado antes de confirmar datos: se llena con su ficha.
		const guardados: any = d.studentData || {};
		setStudentData(d.studentId && d.existingStudent && vacio(guardados.firstName)
			? datosDeFicha(d.existingStudent)
			: { tagIds: [], links: { facebook: "", instagram: "" }, countryId, nationalityId: countryId, ...guardados,
				//Un borrador guardado sin contactos reabre con su renglón vacío (antes solo salía «Agregar Contacto»).
				contacts: (guardados.contacts || []).length > 0 ? guardados.contacts : [{ name: "", relation: "", contactMedia: "" }] });
		setPicks((d.schedules || []).map((row) => ({ scheduleId: row.scheduleId, notes: row.notes || "" })));
		setAdvisors((d.advisors || []).map((a) => ({ userId: a.userId, name: a.name, sharePercentage: null })));
		setPayment({ ...emptyPayment(), ...((d.payment as RequestPaymentDTO) || {}) });
		setPromo(promoDe(d));
		setDocuments(d.documents || []);
		setIssues(d.validation || []);
	};

	// available schedules depend on who the student is (nuevo ingreso vs reingreso rules)
	const effectiveStudentId = studentKind === "existing" ? existingStudent?.id ?? null : null;
	useEffect(() => {
		setSchedules(null);
		http.scheduleService.getAvailableSchedules(campusId, effectiveStudentId)
			.then((r) => sinTalleresNiEventos(campusId, r.data || []))
			.then((lista) => setSchedules(lista))
			.catch((e) => { setSchedules([]); setError(errorText(e)); });
	}, [campusId, effectiveStudentId]);

	useEffect(() => {
		if (isPresencial) {
			setPayment((p) => ({ ...p, typeId: PAYMENT_INTENT_TYPE.EN_CAMINO }));
		} else if (payment.typeId === PAYMENT_INTENT_TYPE.EN_CAMINO) {
			setPayment((p) => ({ ...p, typeId: null }));
		}
	}, [isPresencial]);

	// ---------- student search
	const onStudentQuery = (value: string) => {
		setStudentQuery(value);
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}
		if (value.trim().length < 2) {
			setStudentResults(null);
			return;
		}
		searchTimer.current = setTimeout(() => {
			http.enrollmentRequestsService.findStudents(campusId, value.trim())
				.then((r) => setStudentResults(r.data || []))
				.catch(() => setStudentResults([]));
		}, 400);
	};

	/** Los datos de la ficha del alumno, listos para el formulario (al menos un renglón de contacto). */
	const datosDeFicha = (s: ExistingStudentDTO, encima: any = {}) => {
		const ficha: any = s.studentData || {};
		const conValor = Object.fromEntries(Object.entries(encima).filter(([, v]) => !vacio(v)));
		const juntos: any = { tagIds: [], links: { facebook: "", instagram: "" }, countryId, nationalityId: countryId, ...ficha, ...conValor };
		const reales = (juntos.contacts || []).filter((c: any) => !vacio(c?.name) || !vacio(c?.contactMedia));
		juntos.contacts = reales.length > 0 ? reales : [{ name: "", relation: "", contactMedia: "" }];
		return juntos;
	};

	const pickStudent = (s: ExistingStudentDTO) => {
		setExistingStudent(s);
		setStudentData(datosDeFicha(s));
		setStudentResults(null);
		setStudentQuery("");
		setPicks(picksIniciales());
	};

	const togglePick = (id: number) => setPicks((list) => list.some((p) => p.scheduleId === id)
		? list.filter((p) => p.scheduleId !== id)
		: [...list, { scheduleId: id, notes: "" }]);
	const setPickNotes = (id: number, value: string) => setPicks((list) => list.map((p) => p.scheduleId === id ? { ...p, notes: value } : p));
	const scheduleById = (id: number) => schedules?.find((s) => s.id === id) || null;
	/*
		Un módulo se matricula en un solo horario: al aprobar, WAPP rechaza el segundo (solicitud #27,
		horarios rotativos). Se avisa aquí para que no llegue a Admisiones algo que no se puede aprobar.
	*/
	const modulosRepetidos: string[] = (() => {
		const porModulo: Record<string, AvailableScheduleDTO[]> = {};
		picks.forEach((p) => {
			const sc = scheduleById(p.scheduleId);
			if (sc && sc.levelId) {
				(porModulo[sc.levelId] = porModulo[sc.levelId] || []).push(sc);
			}
		});
		return Object.values(porModulo).filter((g) => g.length > 1).map((g) => `${cursoCorto(g[0].courseName)} · ${g[0].levelName}`);
	})();

	// ---------- student data helpers
	const setField = (field: keyof RequestStudentData, value: any) => setStudentData((d) => ({ ...d, [field]: value }));
	const contacts = studentData.contacts || [];
	const setContact = (index: number, field: "name" | "relation" | "contactMedia", value: string) => {
		const next = contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c));
		setField("contacts", next);
	};

	// ---------- documents
	const onFilePicked = async (event: React.ChangeEvent<HTMLInputElement>, docTypeId: number) => {
		const files = Array.from(event.target.files || []);
		event.target.value = "";
		await agregarArchivos(files, docTypeId);
	};
	/** Los archivos escogidos aquí o tomados con el celular ("Usar el celular"). */
	const agregarArchivos = async (files: File[], docTypeId: number) => {
		if (files.length < 1) {
			return;
		}
		if (files.some((file) => file.size > MAX_UPLOAD_BYTES)) {
			setError(S.fileTooBig);
			return;
		}
		setError(null);
		if (idActual() === null) {
			setPendingFiles((list) => [...list, ...files.map((file) => ({ typeId: docTypeId, file }))]);
		} else {
			const id = idActual()!;
			setBusy(true);
			try {
				for (const file of files) {
					const r = await http.enrollmentRequestsService.uploadDocument(campusId, id, docTypeId, file);
					setDocuments(r.data.documents || []);
					if (!enBorrador) setIssues(r.data.validation || []);   // en borrador, los faltantes salen al guardar a mano
					versionRef.current = r.data.version ?? versionRef.current;
				}
			} catch (e) {
				setError(errorText(e));
			} finally {
				setBusy(false);
			}
		}
	};

	/*
		Leer la identificación con IA (Esteban, 2026-10-19: "que no haya error en datos por los dedazos"): la foto del frente
		llena nombre completo, fecha de nacimiento, género, documento y nacionalidad, y se queda como su identificación.
	*/
	const [lectura, setLectura] = useState<{ texto: string; malo: boolean } | null>(null);
	const [leyendo, setLeyendo] = useState<boolean>(false);
	const fotoIdentificacion = useRef<HTMLInputElement | null>(null);
	const leerIdentificacion = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (file) {
			await procesarFotoId(file);
		}
	};
	const procesarFotoId = async (file: File) => {
		if (file.size > MAX_UPLOAD_BYTES) {
			setError(S.fileTooBig);
			return;
		}
		setLeyendo(true);
		setLectura(null);
		try {
			const r = (await http.enrollmentRequestsService.leerIdentificacion(campusId, file)).data;
			if (r.disponible === false) {
				setLectura({ texto: "La lectura automática no está disponible ahora. Llena los datos a mano.", malo: true });
				return;
			}
			if (!r.leida) {
				setLectura({ texto: r.aviso || "No se pudo leer la identificación. Llena los datos a mano.", malo: true });
				return;
			}
			setStudentData((d) => ({ ...d, ...(r.campos || {}) } as RequestStudentData));
			// La misma foto queda como identificación del alumno (si ya hay matrícula, se sube de una vez).
			if (idActual() === null) {
				setPendingFiles((list) => [...list, { typeId: REQUEST_DOCUMENT_TYPE.ID_ALUMNO, file }]);
			} else {
				const up = await http.enrollmentRequestsService.uploadDocument(campusId, idActual()!, REQUEST_DOCUMENT_TYPE.ID_ALUMNO, file);
				setDocuments(up.data.documents || []);
				if (!enBorrador) setIssues(up.data.validation || []);
				versionRef.current = up.data.version ?? versionRef.current;
			}
			const dudas = r.confianza !== "alta" && r.dudas && r.dudas.length > 0 ? " Ojo: " + r.dudas[0] : "";
			setLectura({ texto: "Datos leídos de la identificación de " + (r.nombreCompleto || "la persona") + ". Revísalos antes de seguir." + dudas, malo: r.confianza === "baja" });
		} catch (e) {
			setLectura({ texto: errorText(e), malo: true });
		} finally {
			setLeyendo(false);
		}
	};

	const removeDocument = async (doc: RequestDocumentDTO) => {
		const id = details?.id ?? requestId;
		if (id === null || id === undefined) {
			return;
		}
		setBusy(true);
		try {
			const r = await http.enrollmentRequestsService.deleteDocument(campusId, id, doc.id);
			setDocuments(r.data.documents || []);
			setIssues(r.data.validation || []);
			versionRef.current = r.data.version ?? versionRef.current;
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	const viewDocument = (doc: RequestDocumentDTO) => {
		const id = details?.id ?? requestId;
		if (id === null || id === undefined) {
			return;
		}
		// Sin await antes de abrirArchivo: la pestaña se abre en el mismo clic (en celular se bloqueaba).
		abrirArchivo({ origen: "Matrículas", nombre: doc.name || doc.type || "Documento", cargar: () => http.enrollmentRequestsService.downloadDocument(campusId, id, doc.id).then((r) => r.data) })
			.catch((e) => setError(errorText(e)));
	};

	// ---------- persist
	const buildPayload = (submit: boolean, promoBase: RequestPromotionDTO = promo) => ({
		modeId,
		studentId: studentKind === "existing" ? existingStudent?.id ?? null : null,
		//Con reingreso también va: lo confirmado se guarda en su ficha al aprobar (Esteban, 2026-09-25).
		studentData: studentKind === "new" || existingStudent ? {
			...studentData,
			contacts: contacts.filter((c) => (c.name || "").trim() !== "" || (c.contactMedia || "").trim() !== ""),
			stateName: states.find((s) => String(s.id) === String(studentData.stateId))?.name || null,
			cityName: (() => { const c = cities.find((x) => String(x.id) === String(studentData.cityId)); return c ? (c.label || c.name) : null; })()
		} : null,
		schedules: picks.map((p) => ({ scheduleId: p.scheduleId, notes: p.notes.trim() })),
		advisors: (advisors || []).map((a) => ({ userId: a.userId, name: a.name })),
		//Solo al crearla: la liga con la persona de la pre-solicitud y le copia sus comprobantes.
		...(prefill && !details ? { prerequestPersonId: prefill.personId } : {}),
		//Monto, cuenta y referencia los llena Caja con el comprobante enfrente (Esteban, 2026-09-22).
		payment: { notes: payment.notes || null, typeId: isPresencial ? PAYMENT_INTENT_TYPE.EN_CAMINO : PAYMENT_INTENT_TYPE.DEPOSITO },
		// «Su otro curso» (2x1 / 2do curso) se liga a ESTA solicitud: en cuanto se sabe su número va en el vínculo (el servidor
		// también lo completa al guardar el borrador nuevo).
		promotion: { items: picks.map((p) => promoBase.items.find((i) => i.scheduleId === p.scheduleId) || { scheduleId: p.scheduleId, promotionId: null })
			.map((it: any) => it.link && it.link.type === "solicitud" && !it.link.requestId && (details?.id ?? requestId)
				? { ...it, link: { ...it.link, requestId: details?.id ?? requestId } } : it),
			referredBy: promoBase.referredBy || null, override10: promoBase.override10 || null },
		...(bundleWith && !details ? { bundleWith: bundleWith.requestId } : {}),
		submit
	});

	const unaVez = useUnaALaVez();
	/*
		¿Hay cambios sin guardar? La firma de lo que se mandaría al guardar, contra la de lo último cargado o guardado. Escape o la X
		ya no cierran los 10 pasos sin preguntar (QA 2026-10-09); se pregunta en la app, no con window.confirm.
	*/
	const firmaGuardada = useRef<string | null>(null);
	const refirmar = useRef<boolean>(false);
	const firmaActual = (): string => { try { return JSON.stringify(buildPayload(false)) + "|" + pendingFiles.length; } catch (e) { return ""; } };
	useEffect(() => {
		if (loading) return;
		if (firmaGuardada.current === null || refirmar.current) {
			refirmar.current = false;
			firmaGuardada.current = firmaActual();
		}
	});
	const [confirmarSalir, setConfirmarSalir] = useState<boolean>(false);
	const [angosto, setAngosto] = useState<boolean>(typeof window !== "undefined" && window.innerWidth <= 600);
	useEffect(() => {
		const f = () => setAngosto(window.innerWidth <= 600);
		window.addEventListener("resize", f);
		return () => window.removeEventListener("resize", f);
	}, []);
	const cerrar = () => {
		if (busy) return;
		if (!loading && firmaGuardada.current !== null && firmaActual() !== firmaGuardada.current) {
			setConfirmarSalir(true);
			return;
		}
		onClose();
	};
	const persist = (submit: boolean, quedarse: boolean = false) => unaVez(() => persistAhora(submit, quedarse));
	const persistAhora = async (submit: boolean, quedarse: boolean = false) => {
		if (submit) {
			const question = details?.returnedByCaja ? S.confirmResendCaja
				: (matriculaDirecta ? (isPresencial ? S.confirmPresencial : S.confirmDirectaDigital) : S.confirmSubmit);
			if (!window.confirm(question)) {
				return;
			}
		}
		clearTimeout(autoTimer.current);
		if (autoEnCurso.current) {
			await autoEnCurso.current;
		}
		setBusy(true);
		setError(null);
		try {
			const id = idActual();
			let saved: EnrollmentRequestDetailsDTO = id === null
				? (await http.enrollmentRequestsService.create(campusId, buildPayload(false))).data
				: (await http.enrollmentRequestsService.update(campusId, id, { ...buildPayload(false), version: versionRef.current })).data;
			// Los que el guardado automático ya subió no se repiten.
			const archivos = pendientesRef.current.slice();
			for (const pending of archivos) {
				saved = (await http.enrollmentRequestsService.uploadDocument(campusId, saved.id, pending.typeId, pending.file)).data;
			}
			setPendingFiles((list) => list.filter((x) => !archivos.includes(x)));
			refirmar.current = true;   // lo guardado es la nueva referencia de «sin cambios»
			setDetails(saved);
			detallesRef.current = saved;
			versionRef.current = saved.version ?? null;
			// La referencia de «sin cambios» lleva el paso Promoción como lo devuelve el servidor (abajo se vuelve a poner así): si no,
			// el guardado automático vería «cambio» y guardaría otra vez sin que nadie tocara nada.
			ultimoGuardado.current = firmaDe(buildPayload(false, promoDe(saved)));
			// La copia del servidor nunca trae «approve»: así la casilla «Apruebo la excepción» no se queda marcada y no re-aprueba en
			// cada guardado (limpiarExcepcion vuelve a aprobar si llega approve: true).
			setPromo(promoDe(saved));
			setDocuments(saved.documents || []);
			setIssues(saved.validation || []);
			if (!submit) {
				if (!quedarse) {
					onSaved(saved, "saved");
				}
				return;
			}
			/*
				No se frena aquí con saved.validation: esa lista es la de APROBAR (incluye «falta que Supervisión apruebe la excepción»).
				Quien decide si se puede ENVIAR es el servidor, que no cuenta la excepción ya pedida (va a revisión). Si no se puede,
				se enseña la lista de siempre.
			*/
			let submitted: EnrollmentRequestDetailsDTO;
			try {
				submitted = (await http.enrollmentRequestsService.submit(campusId, saved.id)).data;
			} catch (e) {
				setError((saved.validation || []).length > 0 ? S.validationTitle + " " + saved.validation.join(" · ") : errorText(e));
				return;
			}
			onSaved(submitted, details?.returnedByCaja ? "resent" : (submitted.statusId === REQUEST_STATUS.APROBADA ? "approved" : "submitted"));
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};

	/*
		Guardado automático (Esteban, 2026-10-19: "¿vamos guardando borradores?"): en cuanto el borrador tiene un nombre, un
		número de identificación, un alumno registrado o una foto, se guarda solo unos segundos después de cada cambio. Así se
		empieza en el celular y se sigue en la PC (pestaña Borradores) y nada se pierde si se cierra. Solo borradores propios
		nuevos o ya en borrador; lo que viene de una Solicitud ("Completar") o ya se envió se sigue guardando a mano.
	*/
	/** Lo que cuenta como "cambió": sin los nombres de estado y ciudad, que llegan después con los catálogos. */
	const firmaDe = (p: any) => JSON.stringify({ ...p, studentData: p.studentData ? { ...p.studentData, stateName: null, cityName: null } : null });
	const enBorrador = !prefill && (details === null ? requestId === null : details.statusId === REQUEST_STATUS.BORRADOR);
	const tieneAlgo = studentKind === "existing" ? !!existingStudent
		: [studentData.firstName, studentData.lastName, studentData.identificationCode].some((v) => !vacio(v)) || pendingFiles.length > 0;
	const autoGuardar = () => {
		if (autoEnCurso.current || busy) {
			autoTimer.current = setTimeout(() => autoGuardarRef.current(), 2000);
			return;
		}
		const payload = buildPayload(false);
		const firma = firmaDe(payload);
		const archivos = pendientesRef.current.slice();
		if (firma === ultimoGuardado.current && archivos.length === 0) {
			return;
		}
		setAutoEstado({ tipo: "guardando", texto: "Guardando…" });
		autoEnCurso.current = (async () => {
			try {
				const id = idActual();
				let saved: EnrollmentRequestDetailsDTO = id === null
					? (await http.enrollmentRequestsService.create(campusId, payload)).data
					: (await http.enrollmentRequestsService.update(campusId, id, { ...payload, version: versionRef.current })).data;
				for (const pf of archivos) {
					saved = (await http.enrollmentRequestsService.uploadDocument(campusId, saved.id, pf.typeId, pf.file)).data;
				}
				if (archivos.length > 0) {
					setPendingFiles((list) => list.filter((x) => !archivos.includes(x)));
				}
				detallesRef.current = saved;
				versionRef.current = saved.version ?? null;
				ultimoGuardado.current = firma;
				setDetails(saved);
				setDocuments(saved.documents || []);
				// Los faltantes no se enseñan mientras escribe: salen al guardar o enviar a mano, como antes.
				const hora = new Date().toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?([ap])\.?\s?m\.?/i, " $1m");
				setAutoEstado({ tipo: "guardado", texto: "Borrador guardado " + hora });
			} catch (e) {
				setAutoEstado({ tipo: "error", texto: "No se guardó: " + errorText(e) });
			} finally {
				autoEnCurso.current = null;
			}
		})();
	};
	autoGuardarRef.current = autoGuardar;
	useEffect(() => {
		if (loading || !enBorrador || !tieneAlgo) {
			return;
		}
		clearTimeout(autoTimer.current);
		autoTimer.current = setTimeout(() => autoGuardarRef.current(), 2500);
		return () => clearTimeout(autoTimer.current);
	}, [studentData, studentKind, existingStudent, picks, advisors, payment.notes, pendingFiles, modeId, promo, loading]);
	// Lo que llegó del servidor al abrir cuenta como ya guardado: abrir un borrador no lo vuelve a guardar.
	useEffect(() => {
		if (!loading && details && !ultimoGuardado.current) {
			detallesRef.current = details;
			ultimoGuardado.current = firmaDe(buildPayload(false));
		}
	}, [loading]);

	const submitLabel = details?.returnedByCaja ? S.resendToCaja : (matriculaDirecta ? S.submitPresencial : S.submit);
	const minor = studentKind === "new" && isMinor(studentData.birthday || null);
	const inputCls = "form-control";
	const selectCls = "form-select";

	/*
		¿Ya existe en WAPP? (Esteban, 2026-09-24) Mientras se teclea el alumno nuevo se busca su
		identificación, teléfono y correo. Si alguno ya es de un alumno registrado se enseña su
		tarjeta y hay que decidir: "Sí, es este alumno" (pasa a Alumno Actual o Reingreso y salta a
		Cursos) o "No es el mismo". El servidor igual lo detecta al guardar por la identificación.
	*/
	const [repetidos, setRepetidos] = useState<{ alumno: ExistingStudentDTO; por: string[] }[]>([]);
	const [descartados, setDescartados] = useState<number[]>([]);
	const timerRepetido = useRef<any>(null);
	const soloDigitos = (v: any) => String(v || "").replace(/\D/g, "");
	useEffect(() => {
		if (studentKind !== "new" || !basicInfo) {
			return;
		}
		if (timerRepetido.current) {
			clearTimeout(timerRepetido.current);
		}
		timerRepetido.current = setTimeout(async () => {
			const codigo = String(studentData.identificationCode || "").toUpperCase().replace(/\s+/g, "");
			const tel = soloDigitos(studentData.phone1);
			const correo = String(studentData.email || "").trim().toLowerCase();
			const busquedas: { q: string; por: string; coincide: (a: ExistingStudentDTO) => boolean }[] = [];
			if (codigo.length >= 5) {
				busquedas.push({ q: codigo, por: "la misma identificación", coincide: (a) => String(a.identificationCode || "").toUpperCase().replace(/\s+/g, "") === codigo });
			}
			if (tel.length >= 10) {
				const cola = tel.slice(-8);
				busquedas.push({ q: tel.slice(-10), por: "el mismo teléfono", coincide: (a) => soloDigitos(a.phone1).slice(-8) === cola });
			}
			if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(correo)) {
				busquedas.push({ q: correo, por: "el mismo correo", coincide: (a) => String(a.email || "").trim().toLowerCase() === correo });
			}
			const encontrados: { [id: number]: { alumno: ExistingStudentDTO; por: string[] } } = {};
			for (const b of busquedas) {
				try {
					const r = await http.enrollmentRequestsService.findStudents(campusId, b.q);
					(r.data || []).filter(b.coincide).forEach((a) => {
						encontrados[a.id] = encontrados[a.id] || { alumno: a, por: [] };
						encontrados[a.id].por.push(b.por);
					});
				} catch (e) {
					// Si la búsqueda falla no se estorba: el servidor vuelve a revisar al guardar.
				}
			}
			setRepetidos(Object.values(encontrados));
		}, 600);
	}, [studentKind, studentData.identificationCode, studentData.phone1, studentData.email]);
	const repetidosPendientes = repetidos.filter((x) => !descartados.includes(x.alumno.id));

	// ---------- pasos
	const movil = useEsTelefono();
	/*
		Reingreso / alumno actual: también pasa por los datos, ya llenos con su ficha, para confirmarlos o
		corregirlos (Esteban, 2026-09-25: el reingreso traía otro contacto de emergencia y no había dónde ponerlo).
	*/
	const pasos = PASOS.filter((p) => !p.soloNuevo || studentKind === "new" || !!existingStudent);
	const [pasoKey, setPasoKey] = useState<PasoKey>("inicio");
	//Si la asesora mandó los datos, la supervisora puede saltar a cualquier paso (o directo a Revisar).
	const [visitados, setVisitados] = useState<PasoKey[]>(prefill?.studentData && Object.keys(prefill.studentData).length > 0 ? PASOS.map((p) => p.key) : ["inicio"]);
	const [intentados, setIntentados] = useState<PasoKey[]>([]);
	const [haciaAtras, setHaciaAtras] = useState<boolean>(false);
	const cuerpo = useRef<HTMLDivElement | null>(null);
	const indice = Math.max(0, pasos.findIndex((p) => p.key === pasoKey));
	const paso = pasos[indice];

	// Al abrir una solicitud que ya existe se entra directo a "Revisar": ahí se ve qué le falta.
	const abrioExistente = useRef<boolean>(false);
	useEffect(() => {
		if (!loading && requestId !== null && !abrioExistente.current) {
			abrioExistente.current = true;
			setPasoKey("revisar");
			setVisitados(PASOS.map((p) => p.key));
		}
	}, [loading, requestId]);

	//Los comprobantes de la pre-solicitud cuentan desde antes de guardar: el servidor los copia al crearla.
	const deLaPreLista = (typeId: number) => prefill && !details ? prefill.documentos.filter((d) => d.typeId === typeId) : [];
	const deLaPre = (typeId: number) => deLaPreLista(typeId).length;
	const docsDe = (typeId: number) => documents.filter((d) => d.typeId === typeId).length + pendingFiles.filter((p) => p.typeId === typeId).length + deLaPre(typeId);

	/** Lo que falta en cada paso, con el nombre del campo tal como se ve en pantalla. */
	const faltanEn = (key: PasoKey): string[] => {
		const f = S.fields;
		const falta: string[] = [];
		const pedir = (valor: any, nombre: string) => { if (vacio(valor)) falta.push(nombre); };
		switch (key) {
			case "inicio":
				if (studentKind === "existing" && !existingStudent) falta.push("Elige al alumno");
				break;
			case "alumno":
				pedir(studentData.firstName, f.firstName);
				pedir(studentData.lastName, f.lastName);
				pedir(studentData.birthday, f.birthday);
				pedir(studentData.genderId, f.genderId);
				pedir(studentData.identificationTypeId, f.identificationTypeId);
				pedir(studentData.identificationCode, f.identificationCode);
				{
					const malId = problemaIdentificacion(studentData.identificationCode, identificationType);
					if (malId) falta.push(malId);
				}
				if (repetidosPendientes.some((x) => x.por.includes("la misma identificación"))) falta.push("Decide si es el alumno que ya existe");
				break;
			case "contacto":
				pedir(studentData.email, f.email);
				// El campo de teléfono deja "+52" aunque se borre el número: eso cuenta como vacío.
				if (String(studentData.phone1 || "").replace(/\D/g, "").length < 8) falta.push(f.phone1);
				if (repetidosPendientes.length > 0) falta.push("Decide si es el alumno que ya existe");
				break;
			case "domicilio":
				pedir(studentData.nationalityId, f.nationalityId);
				pedir(studentData.countryId, f.countryId);
				if (states.length > 0) pedir(studentData.stateId, f.stateId);
				if (cities.length > 0) pedir(studentData.cityId, f.cityId);
				pedir(studentData.address, f.address);
				break;
			case "estudios":
				pedir(studentData.academicDegreeId, f.academicDegreeId);
				//"Última escuela" ya no es obligatoria (Esteban, 2026-09-24).
				pedir(studentData.currentOccupation, f.currentOccupation);
				break;
			case "emergencia":
				if (!contacts.some((c) => !vacio(c.name) && !vacio(c.contactMedia))) falta.push("Un contacto con nombre y teléfono");
				break;
			case "cursos":
				if (picks.length < 1) falta.push("Elige al menos un horario");
				modulosRepetidos.forEach((m) => falta.push(`Un solo horario de ${m}`));
				picks.filter((p) => vacio(p.notes)).forEach((p) => {
					const sc = scheduleById(p.scheduleId);
					falta.push(`${S.notes} de ${sc ? cursoCorto(sc.courseName) : "un curso"}`);
				});
				if (advisors === null) falta.push(S.advisors);
				break;
			case "promocion":
				picks.forEach((p) => {
					const it = promo.items.find((i) => i.scheduleId === p.scheduleId);
					if (!it || it.promotionId === null || it.promotionId === undefined) {
						const sc = scheduleById(p.scheduleId);
						falta.push(`Promoción de ${sc ? cursoCorto(sc.courseName) : "un curso"}`);
					}
				});
				break;
			case "documentos":
				// Un menor no tiene identificación propia: basta la del padre, madre o tutor (Esteban, 2026-10-19).
				if (!minor && docsDe(REQUEST_DOCUMENT_TYPE.ID_ALUMNO) < 1) falta.push(S.docIdStudent);
				if (minor && docsDe(REQUEST_DOCUMENT_TYPE.ID_TUTOR) < 1) falta.push(S.docIdGuardian);
				//Personas que van juntas: con el comprobante en cualquiera de las solicitudes basta (un solo ticket).
				if (!isPresencial && docsDe(REQUEST_DOCUMENT_TYPE.COMPROBANTE) < 1 && !bundleWith && !(details?.bundle || []).length) falta.push(S.docReceipt);
				break;
		}
		return falta;
	};
	const faltaAqui = faltanEn(pasoKey);
	const marcar = intentados.includes(pasoKey);
	const falta = (nombre: string) => marcar && faltaAqui.includes(nombre);
	/** Avisos del servidor (reglas que solo él sabe) que tocan a cada paso. */
	const avisosDe = (key: PasoKey) => issues.filter((a) => pasoDeAviso(a) === key);
	const pendientesDe = (key: PasoKey) => faltanEn(key).length + avisosDe(key).length;
	const pasoListo = (key: PasoKey) => visitados.includes(key) && key !== pasoKey && pendientesDe(key) < 1;
	// Signo "!" en el punto del paso: solo después de haber llegado a Revisar, para no regañar antes de tiempo.
	const pasoPendiente = (key: PasoKey) => visitados.includes("revisar") && key !== pasoKey && key !== "revisar" && pendientesDe(key) > 0;

	const irA = (key: PasoKey) => {
		const destino = pasos.findIndex((p) => p.key === key);
		// Al salir de un paso se quitan sus avisos del servidor: si no quedó arreglado, vuelven
		// al guardar en Revisar; si sí, ya no estorban mientras tanto.
		if (key !== pasoKey && pasoKey !== "revisar") {
			setIssues((lista) => lista.filter((a) => pasoDeAviso(a) !== pasoKey));
		}
		setHaciaAtras(destino < indice);
		setPasoKey(key);
		setVisitados((v) => v.includes(key) ? v : [...v, key]);
		setError(null);
		if (cuerpo.current) {
			cuerpo.current.scrollTop = 0;
		}
		// Al llegar a Revisar se guarda el borrador: así no se pierde lo escrito y el servidor
		// dice qué le falta con sus propias reglas (tutor, escuela de relleno, etc.).
		if (key === "revisar") {
			persist(false, true);
		}
	};
	const siguiente = () => {
		if (faltaAqui.length > 0) {
			setIntentados((v) => v.includes(pasoKey) ? v : [...v, pasoKey]);
			return;
		}
		if (indice < pasos.length - 1) {
			irA(pasos[indice + 1].key);
		}
	};
	const atras = () => {
		if (indice > 0) {
			irA(pasos[indice - 1].key);
		}
	};

	const todoLoQueFalta = pasos.filter((p) => p.key !== "revisar").map((p) => ({ paso: p, faltan: faltanEn(p.key), avisos: avisosDe(p.key) }));
	const totalFaltan = todoLoQueFalta.reduce((n, x) => n + x.faltan.length, 0) + issues.length;
	const avance = Math.round((indice / Math.max(1, pasos.length - 1)) * 100);

	// ---------- piezas
	const Opcion: React.FC<{ activa: boolean; icono: any; titulo: string; onClick: () => void }> = ({ activa, icono, titulo, onClick }) => (
		<button type="button" className={"waOpcion" + (activa ? " activa" : "")} disabled={busy} onClick={onClick} aria-pressed={activa}>
			<FontAwesomeIcon icon={icono} className="waOpcionIcono" />
			<span>{titulo}</span>
			{activa ? <FontAwesomeIcon icon={solid("circle-check")} className="waOpcionCheck" /> : ""}
		</button>
	);

	const pasoInicio = (
		<>
			{canPresencial ? (
				<>
					<div className="waPasoSub">{S.mode}</div>
					<div className="waOpciones">
						<Opcion activa={modeId === REQUEST_MODE.REMOTA} icono={solid("mobile-screen")} titulo={S.modeRemote} onClick={() => setModeId(REQUEST_MODE.REMOTA)} />
						<Opcion activa={isPresencial} icono={solid("store")} titulo={S.modePresencial} onClick={() => setModeId(REQUEST_MODE.PRESENCIAL)} />
					</div>
				</>
			) : ""}
			<div className="waPasoSub">{S.student}</div>
			<div className="waOpciones">
				<Opcion activa={studentKind === "new"} icono={solid("user-plus")} titulo={S.newStudent} onClick={() => { if (studentKind !== "new") { setExistingStudent(null); setStudentData(datosVacios()); } setStudentKind("new"); setPicks(picksIniciales()); }} />
				<Opcion activa={studentKind === "existing"} icono={solid("user-check")} titulo={S.existingStudent} onClick={() => { if (studentKind !== "existing") { setStudentData(datosVacios()); } setStudentKind("existing"); setPicks(picksIniciales()); }} />
			</div>
			{studentKind === "existing" ? (
				<div style={{ marginTop: 14 }} className={marcar && !existingStudent ? "waPasoFalta" : ""}>
					{existingStudent ? (
						<div className="waElegido">
							<div>
								<b>{existingStudent.name}</b>
								<div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>
									{[existingStudent.identityCode, existingStudent.identificationCode, existingStudent.phone1, existingStudent.email].filter(Boolean).join(" · ")}
								</div>
							</div>
							<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => { setExistingStudent(null); setStudentData(datosVacios()); setPicks(picksIniciales()); }}>{S.changeStudent}</button>
						</div>
					) : (
						<>
							<input className={inputCls} type="text" autoFocus placeholder={S.searchStudent} value={studentQuery} disabled={busy} onChange={(e) => onStudentQuery(e.target.value)} />
							{studentResults !== null ? (
								<div className="waResultados">
									{studentResults.length < 1 ? <div style={{ padding: 12, color: "var(--wa-apagado)" }}>{S.noStudents}</div> : studentResults.map((st) => (
										<button type="button" key={"st_" + st.id} className="waResultado" onClick={() => pickStudent(st)}>
											<span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
												<b>{st.name}</b>
												<Badge bg={st.isActive ? "success" : "secondary"} style={{ fontWeight: 500 }}>{st.isActive ? "Activo" : "Inactivo"}</Badge>
											</span>
											<span style={{ fontSize: 14, color: "var(--wa-apagado)" }}>
												{[st.identityCode, st.identificationCode, st.phone1, st.email].filter(Boolean).join(" · ")}
											</span>
										</button>
									))}
								</div>
							) : ""}
						</>
					)}
				</div>
			) : ""}
		</>
	);

	const usarRegistrado = (a: ExistingStudentDTO) => {
		setStudentKind("existing");
		setExistingStudent(a);
		//Lo que ya escribió manda sobre lo de su ficha: es lo más nuevo.
		setStudentData((d) => datosDeFicha(a, { ...d, contacts: (d.contacts || []).some((c: any) => !vacio(c?.name) || !vacio(c?.contactMedia)) ? d.contacts : null }));
		setPicks(picksIniciales());
		setRepetidos([]);
		setVisitados((v) => v.includes("cursos") ? v : [...v, "cursos"]);
		setHaciaAtras(false);
		setPasoKey("cursos");
	};
	const avisoRepetido = repetidosPendientes.length < 1 ? "" : (
		<div className="waRepetido">
			<div style={{ fontWeight: 700, marginBottom: 6 }}>
				<FontAwesomeIcon icon={solid("user-check")} />&nbsp;{repetidosPendientes.length === 1 ? "Este alumno ya está en WAPP" : "Estos alumnos ya están en WAPP"}
			</div>
			{repetidosPendientes.map(({ alumno: a, por }) => (
				<div key={"rep_" + a.id} className="waRepetidoFila">
					<div>
						<b>{a.name}</b> <Badge bg={a.isActive ? "success" : "secondary"} style={{ fontWeight: 500 }}>{a.isActive ? "Activo" : "Inactivo"}</Badge>
						<div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>
							{[a.identityCode, a.identificationCode, a.phone1, a.email].filter(Boolean).join(" · ")}
						</div>
						<div style={{ fontSize: 14 }}>Tiene {por.join(" y ")}.</div>
					</div>
					<div className="waRepetidoBotones">
						<button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => usarRegistrado(a)}>Sí, Es Este Alumno</button>
						<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setDescartados((d) => [...d, a.id])}>No Es el Mismo</button>
					</div>
				</div>
			))}
		</div>
	);

	const f = S.fields;
	const pasoAlumno = (
		<div className="row">
			{avisoRepetido ? <div className="col-md-12">{avisoRepetido}</div> : ""}
			<div className="col-md-12 mb-3">
				<input ref={fotoIdentificacion} type="file" accept="image/*,application/pdf" hidden onChange={leerIdentificacion} />
				<button type="button" className="btn btn-outline-primary btn-sm" disabled={busy || leyendo} onClick={() => fotoIdentificacion.current?.click()}>
					<FontAwesomeIcon icon={solid("id-card")} /> {leyendo ? "Leyendo la identificación…" : "Llenar con la foto de la identificación"}
				</button>
				{!movil ? (
					<button type="button" className="btn btn-outline-secondary btn-sm" style={{ marginLeft: 8 }} disabled={busy || leyendo} onClick={() => setCelular("id_alumno")}>
						<FontAwesomeIcon icon={solid("mobile-screen")} /> Tomarla con el celular
					</button>
				) : null}
				<div style={{ fontSize: 13, color: "var(--wa-apagado)", marginTop: 4 }}>Foto del frente: se llenan solos el nombre completo, la fecha de nacimiento y el número, sin dedazos.</div>
				{lectura ? <div className="waAviso" style={lectura.malo ? undefined : { background: "var(--wa-azul-suave)", borderLeftColor: "var(--wa-azul)" }}>{lectura.texto}</div> : null}
			</div>
			<Field label={f.firstName} required missing={falta(f.firstName)}><input className={inputCls} autoFocus value={studentData.firstName || ""} disabled={busy} onChange={(e) => setField("firstName", e.target.value)} onBlur={(e) => setField("firstName", nombrePropioTexto(e.target.value))} /></Field>
			<Field label={f.lastName} required missing={falta(f.lastName)}><input className={inputCls} value={studentData.lastName || ""} disabled={busy} onChange={(e) => setField("lastName", e.target.value)} onBlur={(e) => setField("lastName", nombrePropioTexto(e.target.value, true))} /></Field>
			<Field label={f.birthday} required missing={falta(f.birthday)}>
				<input className={inputCls} type="date" value={studentData.birthday || ""} disabled={busy} max={todayIso()} onChange={(e) => setField("birthday", e.target.value)} />
				{minor ? <div className="waAviso">Es menor de edad: más adelante te pediremos a su padre, madre o tutor.</div> : ""}
			</Field>
			<Field label={f.genderId} required missing={falta(f.genderId)}>
				<select className={selectCls} value={studentData.genderId ?? ""} disabled={busy} onChange={(e) => setField("genderId", e.target.value)}>
					<option value="">{S.select}</option>
					{(basicInfo?.genders || []).map((g: any) => <option key={"g_" + g.id} value={g.id}>{g.name}</option>)}
				</select>
			</Field>
			<Field label={f.identificationTypeId} required missing={falta(f.identificationTypeId)}>
				<select className={selectCls} value={studentData.identificationTypeId || ""} disabled={busy} onChange={(e) => setField("identificationTypeId", e.target.value)}>
					<option value="">{S.select}</option>
					{(basicInfo?.identificationTypes || []).map((t: any) => <option key={"it_" + t.id} value={t.id}>{t.name}</option>)}
				</select>
			</Field>
			<Field label={f.identificationCode} required missing={falta(f.identificationCode)}>
				{/* En los que son solo números (DNI) se quitan guiones y espacios al escribir: "0801-1999-12345" pasa a 13 dígitos. */}
				<input className={inputCls} value={studentData.identificationCode || ""} disabled={busy} inputMode={identificationType?.bool_only_digits && Number(identificationType?.country_id) === 2 ? "numeric" : undefined}
					maxLength={identificationType?.max_length || 50}
					onChange={(e) => setField("identificationCode", identificationType?.bool_only_digits && Number(identificationType?.country_id) === 2
						? e.target.value.replace(/\D+/g, "")
						: e.target.value.toUpperCase().replace(/\s+/g, ""))} />
				{problemaIdentificacion(studentData.identificationCode, identificationType)
					? <div style={{ fontSize: 13, color: "var(--wa-oro)", marginTop: 4 }}>{problemaIdentificacion(studentData.identificationCode, identificationType)}</div>
					: null}
			</Field>
		</div>
	);

	//Teléfono 1 y 2 no pueden ser el mismo número, con o sin clave de país (Esteban, 2026-09-27).
	const telefonoRepetido = mismoTelefono(studentData.phone1, studentData.phone2);
	const incompleto1 = telefonoIncompleto(studentData.phone1);
	const incompleto2 = telefonoIncompleto(studentData.phone2);
	const correoMalo = correoInvalido(studentData.email);
	const pasoContacto = (
		<div className="row">
			{avisoRepetido ? <div className="col-md-12">{avisoRepetido}</div> : ""}
			<Field label={f.phone1} required missing={falta(f.phone1) || !!incompleto1}>
				<PhoneField id="reqPhone1" name="phone1" value={studentData.phone1 || ""} campusCountryId={countryId}
					disabled={busy} required onChange={(value: string) => setField("phone1", value)} />
				{incompleto1 ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>{incompleto1}</div> : ""}
			</Field>
			<Field label={f.phone2} missing={telefonoRepetido || !!incompleto2}>
				<PhoneField id="reqPhone2" name="phone2" value={studentData.phone2 || ""} campusCountryId={countryId}
					disabled={busy} onChange={(value: string) => setField("phone2", value)} />
				{telefonoRepetido ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>Es el mismo número que el Teléfono 1: déjalo vacío o pon otro número.</div> : ""}
				{!telefonoRepetido && incompleto2 ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>{incompleto2}</div> : ""}
			</Field>
			<Field label={f.email} required missing={falta(f.email) || !!correoMalo} className="col-md-12">
				<input className={inputCls} type="email" inputMode="email" value={studentData.email || ""} disabled={busy} onChange={(e) => setField("email", e.target.value)} onBlur={(e) => setField("email", e.target.value.trim().toLowerCase())} />
				{correoMalo ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>{correoMalo}</div> : ""}
			</Field>
			<Field label={f.facebook}><input className={inputCls} value={studentData.links?.facebook || ""} disabled={busy} onChange={(e) => setField("links", { ...(studentData.links || {}), facebook: e.target.value })} /></Field>
			<Field label={f.instagram}><input className={inputCls} value={studentData.links?.instagram || ""} disabled={busy} onChange={(e) => setField("links", { ...(studentData.links || {}), instagram: e.target.value })} /></Field>
		</div>
	);

	const pasoDomicilio = (
		<div className="row">
			<Field label={f.nationalityId} required missing={falta(f.nationalityId)}>
				<select className={selectCls} value={studentData.nationalityId || ""} disabled={busy} onChange={(e) => setField("nationalityId", e.target.value)}>
					<option value="">{S.select}</option>
					{(basicInfo?.countries || []).map((c: any) => <option key={"n_" + c.id} value={c.id}>{c.name}</option>)}
				</select>
			</Field>
			<Field label={f.countryId} required missing={falta(f.countryId)}>
				<select className={selectCls} value={studentData.countryId || ""} disabled={busy} onChange={(e) => setStudentData((d) => ({ ...d, countryId: e.target.value, stateId: "", cityId: "" }))}>
					<option value="">{S.select}</option>
					{(basicInfo?.countries || []).filter((c: any) => c.bool_campus_exclusive_use < 1).map((c: any) => <option key={"c_" + c.id} value={c.id}>{c.name}</option>)}
				</select>
			</Field>
			<Field label={f.stateId} required={states.length > 0} missing={falta(f.stateId)}>
				{states.length > 0 ? (
					<select className={selectCls} value={studentData.stateId || ""} disabled={busy} onChange={(e) => setStudentData((d) => ({ ...d, stateId: e.target.value, cityId: "" }))}>
						<option value="">{S.select}</option>
						{states.map((st: any) => <option key={"s_" + st.id} value={st.id}>{st.name}</option>)}
					</select>
				) : <input className={inputCls} value={studentData.stateName || ""} disabled={busy} onChange={(e) => setField("stateName", e.target.value)} onBlur={(e) => setField("stateName", textoPropio(e.target.value))} />}
			</Field>
			<Field label={f.cityId} required={cities.length > 0} missing={falta(f.cityId)}>
				{cities.length > 0 ? (
					<select className={selectCls} value={studentData.cityId || ""} disabled={busy} onChange={(e) => setField("cityId", e.target.value)}>
						<option value="">{S.select}</option>
						{cities.map((c: any) => <option key={"ci_" + c.id} value={c.id}>{c.label || c.name}</option>)}
					</select>
				) : <input className={inputCls} value={studentData.cityName || ""} disabled={busy} onChange={(e) => setField("cityName", e.target.value)} onBlur={(e) => setField("cityName", textoPropio(e.target.value))} />}
			</Field>
			<Field label={f.address} required missing={falta(f.address)} className="col-md-8"><input className={inputCls} value={studentData.address || ""} disabled={busy} onChange={(e) => setField("address", e.target.value)} onBlur={(e) => setField("address", textoPropio(e.target.value))} /></Field>
			<Field label={f.zipcode} className="col-md-4"><input className={inputCls} inputMode="numeric" value={studentData.zipcode || ""} disabled={busy} onChange={(e) => setField("zipcode", e.target.value)} /></Field>
		</div>
	);

	const pasoEstudios = (
		<div className="row">
			<Field label={f.academicDegreeId} required missing={falta(f.academicDegreeId)}>
				<select className={selectCls} value={studentData.academicDegreeId || ""} disabled={busy} onChange={(e) => setField("academicDegreeId", e.target.value)}>
					<option value="">{S.select}</option>
					{academicDegrees.map((d: any) => <option key={"ad_" + d.id} value={d.id}>{d.name}</option>)}
				</select>
			</Field>
			<Field label={f.lastSchoolName}><input className={inputCls} value={studentData.lastSchoolName || ""} disabled={busy} onChange={(e) => setField("lastSchoolName", e.target.value)} onBlur={(e) => setField("lastSchoolName", textoPropio(e.target.value))} /></Field>
			<Field label={f.currentOccupation} required missing={falta(f.currentOccupation)}><input className={inputCls} value={studentData.currentOccupation || ""} disabled={busy} onChange={(e) => setField("currentOccupation", e.target.value)} onBlur={(e) => setField("currentOccupation", textoPropio(e.target.value))} /></Field>
			<Field label={f.observations} className="col-md-12"><textarea className={inputCls} rows={2} value={studentData.observations || ""} disabled={busy} onChange={(e) => setField("observations", e.target.value)} /></Field>
		</div>
	);

	const pasoEmergencia = (
		<>
			{minor ? <div className="waAviso" style={{ marginBottom: 12 }}>Es menor de edad: el primer contacto debe ser su padre, madre o tutor.</div> : ""}
			{contacts.map((c, i) => (
				<div key={"ct_" + i} className={"waTarjeta" + (marcar && faltaAqui.length > 0 && i === 0 ? " waPasoFalta" : "")}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
						<b style={{ fontSize: 15 }}>Contacto {i + 1}</b>
						{contacts.length > 1 ? <button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => setField("contacts", contacts.filter((_, j) => j !== i))}>{S.remove}</button> : ""}
					</div>
					<div className="row">
						<Field label={f.contactName} className="col-md-5"><input className={inputCls} value={c.name} disabled={busy} onChange={(e) => setContact(i, "name", e.target.value)} onBlur={(e) => setContact(i, "name", nombrePropioTexto(e.target.value))} /></Field>
						<Field label={f.contactRelation} className="col-md-3"><input className={inputCls} value={c.relation} disabled={busy} onChange={(e) => setContact(i, "relation", e.target.value)} onBlur={(e) => setContact(i, "relation", primeraMayuscula(e.target.value))} /></Field>
						<Field label={f.contactMedia} className="col-md-4"><input className={inputCls} inputMode="tel" value={c.contactMedia} disabled={busy} onChange={(e) => setContact(i, "contactMedia", e.target.value)} /></Field>
					</div>
				</div>
			))}
			<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setField("contacts", [...contacts, { name: "", relation: "", contactMedia: "" }])}>
				<FontAwesomeIcon icon={solid("plus")} />&nbsp;{S.addContact}
			</button>
		</>
	);

	const pasoCursos = (
		<>
			{studentKind === "existing" && existingStudent ? (
				<div className="waElegido" style={{ marginBottom: 14 }}>
					<div><b>{existingStudent.name}</b> <span style={{ fontSize: 14, color: "var(--wa-apagado)" }}>· {S.existingStudent} · {existingStudent.identityCode || existingStudent.identificationCode || ""}</span></div>
				</div>
			) : ""}
			<SchedulePicker schedules={schedules} picked={picks.map((p) => p.scheduleId)}
				onToggle={(schedule) => togglePick(schedule.id)} filter={scheduleFilter} onFilter={setScheduleFilter}
				disabled={busy} title={S.schedule} />
			{modulosRepetidos.length > 0 ? (
				<div className="waRepetido" style={{ marginTop: 12 }}>
					<div style={{ fontWeight: 700, marginBottom: 4 }}>
						<FontAwesomeIcon icon={solid("triangle-exclamation")} />&nbsp;Elegiste dos horarios del mismo módulo
					</div>
					<div style={{ fontSize: 15 }}>
						{modulosRepetidos.join(", ")}: un módulo se matricula en un solo horario. Si toma horarios rotativos, deja uno y anota el otro en la nota de matrícula.
					</div>
				</div>
			) : ""}
			{picks.length > 0 ? <div className="waPasoSub" style={{ marginTop: 16 }}>{S.notes}</div> : ""}
			{/* Una nota de matrícula por cada curso elegido */}
			{picks.map((pick) => {
				const schedule = scheduleById(pick.scheduleId);
				const sinNota = marcar && vacio(pick.notes);
				return (
					<div key={"pick_" + pick.scheduleId} className={"waTarjeta" + (sinNota ? " waPasoFalta" : "")}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
							<div>
								<b>{schedule ? cursoCorto(schedule.courseName) : `Horario #${pick.scheduleId}`}</b>
								{schedule ? <div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>{horarioBonito(schedule.name, { plural: true, separador: " " })}</div> : ""}
							</div>
							<button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => togglePick(pick.scheduleId)}>{S.remove}</button>
						</div>
						<textarea className={inputCls} rows={2} placeholder={S.notesPlaceholder} value={pick.notes} disabled={busy}
							onChange={(e) => setPickNotes(pick.scheduleId, e.target.value)} />
						{sinNota ? <div style={{ fontSize: 14, color: "var(--wa-oro)", marginTop: 3 }}>{S.notes} · falta</div> : ""}
					</div>
				);
			})}
			<div style={{ marginTop: 14, maxWidth: 520 }}>
				<AdvisorsPicker campusId={campusId} value={advisors}
					onChange={(list) => setAdvisors(list)} disabled={busy} />
			</div>
		</>
	);

	const pasoDocumentos = (
		<>
			{[
				{ typeId: REQUEST_DOCUMENT_TYPE.ID_ALUMNO, label: S.docIdStudent, required: !minor, show: true },
				{ typeId: REQUEST_DOCUMENT_TYPE.ID_TUTOR, label: S.docIdGuardian, required: true, show: minor },
				{ typeId: REQUEST_DOCUMENT_TYPE.COMPROBANTE, label: S.docReceipt, required: !isPresencial, show: true }
			].filter((fila) => fila.show).map((fila) => {
				const subidos = documents.filter((d) => d.typeId === fila.typeId);
				const enEspera = pendingFiles.filter((p) => p.typeId === fila.typeId);
				const listo = subidos.length + enEspera.length + deLaPre(fila.typeId) > 0;
				return (
					<div key={"dt_" + fila.typeId} className={"waTarjeta" + (marcar && fila.required && !listo ? " waPasoFalta" : "")}>
						<div className="waDocFila">
							<div>
								<b style={{ fontSize: 15 }}>{fila.label}{fila.required ? " *" : ""}</b>
								<div style={{ fontSize: 14, color: listo ? "var(--wa-verde)" : (fila.required ? "var(--wa-oro)" : "var(--wa-apagado)") }}>
									{listo ? <><FontAwesomeIcon icon={solid("circle-check")} />&nbsp;Listo</> : (fila.required ? S.docMissing : S.docOptional)}
								</div>
							</div>
							{/* Botón grande con el input escondido: en el celular abre la cámara o la galería. */}
							<div className="waDocBotones">
								{!movil ? (
									<button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setCelular(fila.typeId === REQUEST_DOCUMENT_TYPE.ID_ALUMNO ? "id_alumno"
										: fila.typeId === REQUEST_DOCUMENT_TYPE.ID_TUTOR ? "id_tutor" : "comprobante")}>
										<FontAwesomeIcon icon={solid("mobile-screen")} />&nbsp;Con el Celular
									</button>
								) : null}
								<label className="btn btn-outline-secondary" style={{ position: "static", margin: 0, opacity: busy ? 0.6 : 1 }}>
									<FontAwesomeIcon icon={solid("paperclip")} />&nbsp;{listo ? "Agregar Otro" : "Subir Archivo"}
									<input type="file" accept={ACCEPTED_FILES} multiple disabled={busy} style={{ display: "none" }} onChange={(e) => onFilePicked(e, fila.typeId)} />
								</label>
							</div>
						</div>
						{subidos.map((d) => (
							<div key={"doc_" + d.id} className="waArchivo">
								<span style={{ wordBreak: "break-all" }}>{d.name}</span>
								<span style={{ display: "flex", gap: 12, flexShrink: 0 }}>
									<button type="button" className="btn btn-link btn-sm" style={{ padding: 0 }} disabled={busy} onClick={() => viewDocument(d)}>{S.download}</button>
									<button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => removeDocument(d)}>{S.remove}</button>
								</span>
							</div>
						))}
						{deLaPre(fila.typeId) > 0 ? deLaPreLista(fila.typeId).map((c) => (
							<div key={"pre_" + c.id} className="waArchivo">
								<span style={{ wordBreak: "break-all" }}>{c.name} <span style={{ color: "var(--wa-apagado)" }}>(solicitud #{prefill!.prerequestId})</span></span>
								<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, flexShrink: 0 }} disabled={busy} onClick={() => prefill!.verDocumento(c.id)}>{S.download}</button>
							</div>
						)) : ""}
						{enEspera.map((pf) => (
							<div key={"pf_" + pf.file.name} className="waArchivo">
								<span style={{ wordBreak: "break-all" }}>{pf.file.name}</span>
								<button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0, flexShrink: 0 }} disabled={busy}
									onClick={() => setPendingFiles(pendingFiles.filter((x) => x !== pf))}>{S.remove}</button>
							</div>
						))}
					</div>
				);
			})}
			<div style={{ marginTop: 6 }}>
				<label style={{ position: "static", fontSize: 14, color: "var(--wa-apagado)", fontWeight: 500, display: "block", marginBottom: 3 }}>{S.paymentNotes}</label>
				<textarea className={inputCls} rows={2} placeholder={S.paymentNotesPlaceholder} value={payment.notes || ""} disabled={busy}
					onChange={(e) => setPayment({ ...payment, notes: e.target.value })} />
			</div>
		</>
	);

	// El último motivo de rechazo (comentario tipo DEVOLUCION = 2): quien corrige necesita tenerlo enfrente.
	const ultimoRechazo = details && details.statusId === REQUEST_STATUS.DEVUELTA
		? [...(details.comments || [])].filter((c) => c.typeId === 2).sort((x, y) => String(y.createdAt).localeCompare(String(x.createdAt)))[0] || null
		: null;
	const avisoRechazo = details && details.statusId === REQUEST_STATUS.DEVUELTA ? (
		<div className="waRechazo">
			<div style={{ fontWeight: 700, marginBottom: 2 }}>
				<FontAwesomeIcon icon={solid("circle-exclamation")} />&nbsp;{details.returnedByCaja ? S.returnedByCaja : "Rechazada"}
				{ultimoRechazo ? <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}> · {ultimoRechazo.createdBy} · {formatLocalDateTime(ultimoRechazo.createdAt)}</span> : ""}
			</div>
			{ultimoRechazo ? <div style={{ whiteSpace: "pre-wrap" }}><b>{S.whatToFix}:</b> {ultimoRechazo.body}</div> : ""}
		</div>
	) : "";

	const nombreAlumno = studentKind === "existing" ? (existingStudent?.name || "") : `${studentData.firstName || ""} ${studentData.lastName || ""}`.trim();
	const pasoRevisar = (
		<>
			{avisoRechazo}
			<div className={"waMeta" + (totalFaltan < 1 ? " lista" : "")}>
				<FontAwesomeIcon icon={totalFaltan < 1 ? solid("circle-check") : solid("flag-checkered")} className="waMetaIcono" />
				<div>
					<div style={{ fontSize: 18, fontWeight: 700 }}>
						{busy ? S.saving : (totalFaltan < 1 ? "¡Todo listo!" : (totalFaltan === 1 ? "Falta 1 cosa" : `Faltan ${totalFaltan} cosas`))}
					</div>
					<div style={{ fontSize: 14, color: "var(--wa-apagado)" }}>
						{nombreAlumno || "Alumno sin nombre"} · {isPresencial ? S.modePresencial : S.modeRemote}
						{picks.length > 0 ? ` · ${picks.length} ${picks.length === 1 ? "curso" : "cursos"}` : ""}
					</div>
				</div>
			</div>
			{todoLoQueFalta.map(({ paso: p, faltan, avisos }) => {
				const mal = faltan.length + avisos.length > 0;
				return (
					<button type="button" key={"rv_" + p.key} className={"waRevisa" + (mal ? " falta" : "")} onClick={() => irA(p.key)}>
						<FontAwesomeIcon icon={mal ? solid("circle-exclamation") : solid("circle-check")} className="waRevisaIcono" />
						<span style={{ flex: 1, textAlign: "left" }}>
							<b>{p.titulo}</b>
							{faltan.length > 0 ? <span className="waRevisaFalta">Falta: {faltan.join(", ")}</span> : ""}
							{/* Lo que solo sabe el servidor: tutor en contactos, escuela de relleno... */}
							{avisos.map((a, i) => <span key={"av_" + i} className="waRevisaFalta">{a}</span>)}
						</span>
						<span className="waRevisaIr">{mal ? "Arreglar" : "Editar"} <FontAwesomeIcon icon={solid("arrow-right")} /></span>
					</button>
				);
			})}
			{avisosDe("revisar").length > 0 ? avisosDe("revisar").map((aviso, i) => (
				<button type="button" key={"is_" + i} className="waRevisa falta" onClick={() => irA(pasoDeAviso(aviso))}>
					<FontAwesomeIcon icon={solid("circle-exclamation")} className="waRevisaIcono" />
					<span style={{ flex: 1, textAlign: "left" }}>{aviso}</span>
					<span className="waRevisaIr">Arreglar <FontAwesomeIcon icon={solid("arrow-right")} /></span>
				</button>
			)) : ""}
		</>
	);

	/** "Agregar otra persona": guarda esta solicitud y abre una nueva que va junto con ella. */
	const agregarPersona = () => unaVez(agregarPersonaAhora);
	const agregarPersonaAhora = async () => {
		if (!onSwitch) return;
		setBusy(true);
		setError(null);
		try {
			const id = details?.id ?? requestId;
			const saved: EnrollmentRequestDetailsDTO = id === null || id === undefined
				? (await http.enrollmentRequestsService.create(campusId, buildPayload(false))).data
				: (await http.enrollmentRequestsService.update(campusId, id, buildPayload(false))).data;
			for (const pending of pendingFiles) {
				await http.enrollmentRequestsService.uploadDocument(campusId, saved.id, pending.typeId, pending.file);
			}
			setPendingFiles([]);
			onSwitch({ requestId: null, bundleWith: { requestId: saved.id, name: nombreAlumno || "Solicitud #" + saved.id } });
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};
	const salirDelGrupo = () => unaVez(salirDelGrupoAhora);
	const salirDelGrupoAhora = async () => {
		const id = details?.id ?? requestId;
		if (id === null || id === undefined || !window.confirm("¿Sacar a esta persona del grupo? Se aprobará sola y las promociones que dependan de ella habrá que revisarlas.")) return;
		setBusy(true);
		try {
			const r = await http.enrollmentRequestsService.update(campusId, id, { ...buildPayload(false), leaveBundle: true });
			hydrate(r.data);
		} catch (e) {
			setError(errorText(e));
		} finally {
			setBusy(false);
		}
	};
	const pasoPromocion = (
		<>
			{bundleWith && !details ? <div className="waAviso" style={{ marginTop: 0, marginBottom: 12 }}>Esta persona se matricula junto con <b>{bundleWith.name}</b> (solicitud #{bundleWith.requestId}): se aprueban juntas y van en un solo ticket.</div> : ""}
			<PasoPromocion campusId={campusId} requestId={details?.id ?? requestId} studentId={studentKind === "existing" ? existingStudent?.id ?? null : null}
				studentName={nombreAlumno} scheduleIds={picks.map((p) => p.scheduleId)} value={promo} onChange={setPromo} busy={busy} marcar={marcar}
				bundle={[...(details?.bundle || []), ...(bundleWith && !details ? [{ id: bundleWith.requestId, name: bundleWith.name, statusId: 1, status: "", schedules: [] }] : [])]}
				onAddPerson={onSwitch ? agregarPersona : undefined}
				onOpenRequest={onSwitch ? (id) => { if (window.confirm("¿Guardar esta y abrir la otra solicitud?")) {
					// Si ya se está guardando, se espera a que termine y luego se guarda y se cambia (no se salta el guardado).
					unaVez.esperar().then(() => persist(false, true)).then(() => onSwitch({ requestId: id })); } } : undefined}
				onLeaveBundle={details && (details.bundle || []).length > 0 ? salirDelGrupo : undefined} />
		</>
	);

	const contenido: Record<PasoKey, React.ReactNode> = {
		inicio: pasoInicio, alumno: pasoAlumno, contacto: pasoContacto, domicilio: pasoDomicilio, estudios: pasoEstudios,
		emergencia: pasoEmergencia, cursos: pasoCursos, promocion: pasoPromocion, documentos: pasoDocumentos, revisar: pasoRevisar
	};

	return (
		<>
		<Modal className="waModulo waModalArriba" backdropClassName="waFondoArriba" show={confirmarSalir} onHide={() => setConfirmarSalir(false)} centered>
			<Modal.Header closeButton><Modal.Title style={{ fontSize: 16 }}>¿Salir sin Guardar?</Modal.Title></Modal.Header>
			<Modal.Body style={{ fontSize: 15 }}>Hay cambios en esta matrícula que no se han guardado. Si sales ahora, se pierden.</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary" style={{ minHeight: 44 }} onClick={() => { setConfirmarSalir(false); onClose(); }}>Salir sin Guardar</button>
				<button type="button" className="btn btn-primary" style={{ minHeight: 44 }} autoFocus onClick={() => setConfirmarSalir(false)}>Seguir Editando</button>
			</Modal.Footer>
		</Modal>
		<Modal className="waModulo waPasos" show onHide={busy ? undefined : cerrar} size="xl" centered scrollable backdrop="static" fullscreen="sm-down">
			<Modal.Header closeButton={!busy} style={{ flexDirection: "column", alignItems: "stretch", gap: 10, position: "relative" }}>
				<Modal.Title style={{ fontSize: movil ? 16 : 18, paddingRight: 32 }}>
					{requestId === null && details === null ? S.newRequest : `${S.detailsTitle} #${details?.id ?? requestId}`}
					{prefill ? <span style={{ fontSize: 14, fontWeight: 500, color: "var(--wa-apagado)", marginLeft: 8 }}>desde la solicitud #{prefill.prerequestId}</span> : ""}
					{bundleWith && !details ? <span style={{ fontSize: 14, fontWeight: 500, color: "var(--wa-apagado)", marginLeft: 8 }}>junto con {bundleWith.name} (#{bundleWith.requestId})</span> : ""}
					{details ? <Badge bg="secondary" style={{ marginLeft: 10, fontWeight: 500 }}>{details.status}</Badge> : ""}
				</Modal.Title>
				{/* Avance: barra + un punto por paso. Los terminados llevan palomita y se puede volver a ellos. En el teléfono (≤ 600 px)
				    los 10 puntos no caben (837 px en 375): «Paso 8 de 10 · Promoción», la barra y una lista para saltar a un paso visto. */}
				{angosto ? (
					<div className="waAvance" aria-label={`Paso ${indice + 1} de ${pasos.length}`}>
						<div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
							<b style={{ fontSize: 14, whiteSpace: "nowrap" }}>Paso {indice + 1} de {pasos.length}</b>
							<select className="form-select form-select-sm" style={{ minWidth: 0, flex: 1, fontSize: 16, minHeight: 44 }} value={pasoKey} disabled={busy}
								aria-label="Ir a otro paso" onChange={(e) => irA(e.target.value as PasoKey)}>
								{pasos.map((p, i) => (
									<option key={"op_" + p.key} value={p.key} disabled={p.key !== pasoKey && !visitados.includes(p.key)}>
										{i + 1}. {p.titulo}{pasoListo(p.key) ? " ✓" : (pasoPendiente(p.key) ? " (falta)" : "")}</option>
								))}
							</select>
						</div>
						<div className="waAvanceBarra"><div style={{ width: `${avance}%` }} /></div>
					</div>
				) : (
				<div className="waAvance" aria-label={`Paso ${indice + 1} de ${pasos.length}`}>
					<div className="waAvanceBarra"><div style={{ width: `${avance}%` }} /></div>
					<div className="waAvancePasos">
						{pasos.map((p, i) => {
							const clase = p.key === pasoKey ? "actual" : (pasoListo(p.key) ? "listo" : (pasoPendiente(p.key) ? "pendiente" : (visitados.includes(p.key) ? "visto" : "")));
							return (
								<button type="button" key={"av_" + p.key} className={"waAvancePaso " + clase} title={p.titulo}
									disabled={busy || !visitados.includes(p.key)} onClick={() => irA(p.key)}>
									<span className="waAvancePunto">{clase === "listo" ? <FontAwesomeIcon icon={solid("check")} /> : (clase === "pendiente" ? "!" : i + 1)}</span>
									{movil ? "" : <span className="waAvanceNombre">{p.titulo}</span>}
								</button>
							);
						})}
					</div>
				</div>
				)}
			</Modal.Header>
			<Modal.Body ref={cuerpo as any} style={{ textAlign: "left", fontSize: 16, padding: movil ? "16px 16px 24px" : "22px 28px 28px" }}>
				{loading ? <div className="loader" /> : (
					<div key={pasoKey} className={"waPaso " + (haciaAtras ? "atras" : "adelante")} style={{ maxWidth: pasoKey === "cursos" ? undefined : 820, margin: "0 auto" }}>
						<div className="waPasoCabeza">
							<span className="waPasoIcono"><FontAwesomeIcon icon={paso.icono} /></span>
							<div>
								<div className="waPasoNumero">Paso {indice + 1} de {pasos.length}</div>
								<h4 className="waPasoPregunta">{paso.pregunta}</h4>
							</div>
						</div>
						{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
						{marcar && faltaAqui.length > 0 ? (
							<div className="waFaltaAviso">
								<FontAwesomeIcon icon={solid("circle-exclamation")} />&nbsp;Te falta: {faltaAqui.join(", ")}
							</div>
						) : ""}
						{/* Promoción enseña sus avisos en vivo (los del servidor al guardar se quedarían viejos). */}
						{pasoKey !== "revisar" && pasoKey !== "promocion" && avisosDe(pasoKey).length > 0 ? (
							<div className="waFaltaAviso">
								{avisosDe(pasoKey).map((a, i) => <div key={"pa_" + i}><FontAwesomeIcon icon={solid("circle-exclamation")} />&nbsp;{a}</div>)}
							</div>
						) : ""}
						{existingStudent && ["alumno", "contacto", "domicilio", "estudios", "emergencia"].includes(pasoKey) ? (
							<div className="waAviso" style={{ marginTop: 0, marginBottom: 14 }}>Son los datos de su perfil: corrige lo que haya cambiado. Se guarda en su perfil al aprobarse.</div>
						) : ""}
						{contenido[pasoKey]}
					</div>
				)}
			</Modal.Body>
			{celular ? (
				<FotoCelular campusId={campusId} tipo={celular} onClose={() => setCelular(null)}
					onFoto={(f) => celular === "id_alumno" ? procesarFotoId(f)
						: agregarArchivos([f], celular === "id_tutor" ? REQUEST_DOCUMENT_TYPE.ID_TUTOR : REQUEST_DOCUMENT_TYPE.COMPROBANTE)} />
			) : null}
			<Modal.Footer className="waPasosPie">
				<div className="waGuardarCaja">
					<button type="button" className="btn btn-link waGuardar" disabled={busy || loading} onClick={() => persist(false)}>
						{busy ? S.saving : (details ? S.save : S.saveDraft)}
					</button>
					{autoEstado && enBorrador ? <span className={"waAutoGuardado" + (autoEstado.tipo === "error" ? " error" : "")} title={autoEstado.texto}>{autoEstado.texto}</span> : null}
				</div>
				<div className="waPasosBotones">
					{indice > 0 ? (
						<button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={atras}>
							<FontAwesomeIcon icon={solid("arrow-left")} />&nbsp;Atrás
						</button>
					) : ""}
					{pasoKey === "revisar" ? (
						<button type="button" className="btn btn-primary" disabled={busy || loading} onClick={() => persist(true)}>
							<FontAwesomeIcon icon={solid("paper-plane")} />&nbsp;{submitLabel}
						</button>
					) : (
						<button type="button" className="btn btn-primary" disabled={busy || loading} onClick={siguiente}>
							Siguiente&nbsp;<FontAwesomeIcon icon={solid("arrow-right")} />
						</button>
					)}
				</div>
			</Modal.Footer>
		</Modal>
		</>
	);
};

export default RequestFormModal;
